import React from 'react';

import { Column } from '@xingternal/xing-grid';

import { JDPModuleName } from '@/components/job-posting/types';
import { ExternalUrlDescription, HtmlDescription } from '@/graphql/generated';
import { useUserTrackingConsent } from '@/hooks';

import { PersonalwerkTracking } from '../personalwerk-tracking';
import { ScrollTrackingModuleWrapper } from '../scroll-tracking-module-wrapper';
import ExternalURLDescriptionComponent from './components/external-url-description';
import { HTMLDescriptionComponent } from './components/html-description';
import PdfDescriptionComponent, { PdfDescriptionProps as PdfDescription } from './components/pdf-description';

import * as S from './job-posting-description.styles';

const descriptionByTypename = {
  HtmlDescription: HTMLDescriptionComponent,
  ExternalUrlDescription: ExternalURLDescriptionComponent,
  PdfDescription: PdfDescriptionComponent,
};

export type JobPostingDescriptionProp = HtmlDescription | ExternalUrlDescription | PdfDescription;

interface DescriptionProps {
  logo?: string | null;
  description: JobPostingDescriptionProp;
  moduleName: JDPModuleName;
  // Not optional. It's needed to know if we should inject Personalwerk's
  // tracking script.
  jobCode: string | null;
}

const isPersonalwerkJob = (jobCode: string | null | undefined): jobCode is string => {
  return typeof jobCode === 'string' && /personalwerk-\d+-\d+/.test(jobCode);
};

export const JobPostingDescription: React.FC<DescriptionProps> = ({ logo, description, moduleName, jobCode }) => {
  const { hasTrackingConsent } = useUserTrackingConsent();
  const Description = descriptionByTypename[description.__typename || 'HtmlDescription'];

  return (
    <ScrollTrackingModuleWrapper moduleTrackingName={moduleName}>
      {hasTrackingConsent && isPersonalwerkJob(jobCode) ? <PersonalwerkTracking jobCode={jobCode} /> : null}
      <S.JobPostingDescriptionContainer>
        {logo && (
          <Column size={12}>
            <S.Descriptionlogo src={logo} data-testid="descriptionLogo" alt="" />
          </Column>
        )}

        <Description description={description} />
      </S.JobPostingDescriptionContainer>
    </ScrollTrackingModuleWrapper>
  );
};
